import { Vue, Component } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import { ClientTypeEnum } from '@/enums/client-type.enum';
import CRMEvoTalksService, {
  ICRMEvoTalksStatusResult,
  ICRMEvoTalksSyncClienteEmpresaResult,
} from '@/services/crm/evotalks.service';
import ClientModel from '@/models/crm/client.model';
import SettingsModel from '@/models/crm/settings.model';

interface IEvoTalksHost {
  client: ClientModel | null;
  clientType: ClientTypeEnum;
  settings: SettingsModel;
}

@Component({})
export default class EvoTalksMixin extends Vue {
  @inject(InjectionIdEnum.CRMEvoTalksService)
  private evotalksService!: CRMEvoTalksService;

  evotalksSyncEmpresaLoading = false;

  evotalksSyncContatosLoading = false;

  evotalksStatus: ICRMEvoTalksStatusResult = {
    empresaSincronizada: false,
    evoClientId: null,
    totalContatos: 0,
    contatosSincronizados: 0,
    ultimaSincronizacaoEmpresa: null,
    ultimaSincronizacaoContatos: null,
  };

  get evotalksClient(): ClientModel | null {
    return (this as unknown as IEvoTalksHost).client;
  }

  get evotalksClientType(): ClientTypeEnum {
    return (this as unknown as IEvoTalksHost).clientType;
  }

  get evotalksSettings(): SettingsModel {
    return (this as unknown as IEvoTalksHost).settings;
  }

  get evotalksSyncing(): boolean {
    return this.evotalksSyncEmpresaLoading || this.evotalksSyncContatosLoading;
  }

  get showEvoTalksActions(): boolean {
    return this.evotalksSettings?.isWhatsAppEvoTalksAtivo
      && (this.evotalksClientType === ClientTypeEnum.Client
        || this.evotalksClientType === ClientTypeEnum.Prospect);
  }

  get syncEntityActionLabel(): string {
    return this.evotalksClientType === ClientTypeEnum.Client
      ? `${this.$t('crm.evoTalksActions.label.syncClient')}`
      : `${this.$t('crm.evoTalksActions.label.syncProspect')}`;
  }

  get isEntitySynced(): boolean {
    return this.evotalksStatus.empresaSincronizada;
  }

  get areContactsSynced(): boolean {
    return this.evotalksStatus.totalContatos > 0
      && this.evotalksStatus.contatosSincronizados >= this.evotalksStatus.totalContatos;
  }

  get syncEntityTooltip(): string {
    return this.formatLastSyncTooltip(this.evotalksStatus.ultimaSincronizacaoEmpresa);
  }

  get syncContactsTooltip(): string {
    return this.formatLastSyncTooltip(this.evotalksStatus.ultimaSincronizacaoContatos);
  }

  resetCRMEvoTalksStatus(): void {
    this.evotalksStatus = {
      empresaSincronizada: false,
      evoClientId: null,
      totalContatos: 0,
      contatosSincronizados: 0,
      ultimaSincronizacaoEmpresa: null,
      ultimaSincronizacaoContatos: null,
    };
  }

  async loadCRMEvoTalksStatus(): Promise<void> {
    const client = this.evotalksClient;
    if (!client?.codCliente) return;
    try {
      if (this.evotalksClientType === ClientTypeEnum.Client) {
        const status = await this.evotalksService.getClienteStatus(client.cnpjCpf ?? null, null);
        this.evotalksStatus = {
          empresaSincronizada: status.clienteSincronizado,
          evoClientId: status.evoClientId,
          totalContatos: status.totalContatos ?? 0,
          contatosSincronizados: status.contatosSincronizados ?? 0,
          ultimaSincronizacaoEmpresa: status.ultimaSincronizacaoEmpresa ?? null,
          ultimaSincronizacaoContatos: status.ultimaSincronizacaoContatos ?? null,
        };
      } else {
        this.evotalksStatus = await this.evotalksService.getStatus(null, Number(client.codCliente));
      }
    } catch {
      // silencioso — status nao e critico
    }
  }

  formatLastSyncTooltip(lastSync: string | null): string {
    if (!lastSync) {
      return `${this.$t('crm.evoTalksActions.label.synced')}`;
    }

    const date = new Date(lastSync);
    if (Number.isNaN(date.getTime())) {
      return `${this.$t('crm.evoTalksActions.label.synced')}`;
    }

    return `${this.$t('crm.evoTalksActions.label.lastSync', { date: this.$d(date, 'dateTime') })}`;
  }

  async onSyncEmpresaCRMEvoTalks(): Promise<void> {
    const client = this.evotalksClient;
    if (!client) return;
    this.evotalksSyncEmpresaLoading = true;
    // Notificar so DEPOIS de fechar o modal (v-dialog persistent cobre o toast)
    let sucesso: string | null = null;
    let aviso: string | null = null;
    let erro: string | null = null;
    try {
      if (this.evotalksClientType === ClientTypeEnum.Client) {
        const result: ICRMEvoTalksSyncClienteEmpresaResult = await this.evotalksService
          .syncClienteEmpresa(client.cnpjCpf ?? null, null);
        aviso = result.aviso ?? null;
        if (result.empresaSincronizada) {
          sucesso = `${this.$t('crm.evoTalksActions.messages.companySynced')}`;
        }
      } else {
        const result = await this.evotalksService.syncEmpresa(null, Number(client.codCliente));
        aviso = result.aviso ?? null;
        if (result.empresaSincronizada) {
          sucesso = `${this.$t('crm.evoTalksActions.messages.companySynced')}`;
        }
      }
      await this.loadCRMEvoTalksStatus();
    } catch (err) {
      erro = `${this.$t('crm.evoTalksActions.messages.companySyncError')}`;
    } finally {
      this.evotalksSyncEmpresaLoading = false;
    }
    // Vinculada, mas a EvoTalks nao foi atualizada (falha na edicao ou dados mantidos
    // pelo cadastro dono): o aviso do GEO.
    if (erro) this.$notify.error(erro);
    else if (aviso) this.$notify.warn(aviso, 8000);
    else if (sucesso) this.$notify.success(sucesso);
  }

  async onSyncContatosCRMEvoTalks(): Promise<void> {
    const client = this.evotalksClient;
    if (!client) return;
    this.evotalksSyncContatosLoading = true;
    // Notificar so DEPOIS de fechar o modal (v-dialog persistent cobre o toast)
    let sucesso: string | null = null;
    let erro: string | null = null;
    try {
      if (this.evotalksClientType === ClientTypeEnum.Client) {
        const result = await this.evotalksService.syncClienteContatos(client.cnpjCpf ?? null, null);
        sucesso = `${this.$t('crm.evoTalksActions.messages.contactsSynced', {
          synced: result.sincronizados,
          total: result.total,
        })}`;
      } else {
        const result = await this.evotalksService.syncContatos(null, Number(client.codCliente));
        sucesso = `${this.$t('crm.evoTalksActions.messages.contactsSynced', {
          synced: result.sincronizados,
          total: result.total,
        })}`;
      }
      await this.loadCRMEvoTalksStatus();
    } catch (err) {
      erro = `${this.$t('crm.evoTalksActions.messages.contactsSyncError')}`;
    } finally {
      this.evotalksSyncContatosLoading = false;
    }
    if (erro) this.$notify.error(erro);
    else if (sucesso) this.$notify.success(sucesso);
  }
}
